import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Button } from '@column/column-ui-kit';

import { DateInput } from '~/components/DateInput';
import { PopoverAction, PopoverBase, PopoverBaseProps } from '~/components/Popover/Base';
import { PopoverFilterTransferDateData } from '~/components/Popover/Filter/TransferDate';
import { Flex, FormElement, FormLabel, Inner } from '~/styles';
import { parseDateTime } from '~/util/dateFilterHelpers';

interface PopoverFilterCreatedDateProps extends PopoverBaseProps {
  data: Partial<PopoverFilterTransferDateData>;
  convertDateRange: (params: {
    startDateString?: string;
    endDateString?: string;
  }) => Partial<PopoverFilterTransferDateData>;
  onSubmit?: (data: Partial<PopoverFilterTransferDateData>) => void;
}

interface DateState {
  date?: string;
}

const Wrapper = styled(PopoverBase)`
  --popover-padding: 0px;
  --popover-width: 248px;
  --popover-left: 0px;
`;

export const PopoverFilterCreatedDate: React.FC<PopoverFilterCreatedDateProps> = ({
  data,
  convertDateRange,
  onSubmit,
  ...popoverProps
}) => {
  const [startDate, setStartDate] = useState<DateState>({});
  const [endDate, setEndDate] = useState<DateState>({});

  useEffect(() => {
    if (data?.createdAtGte) {
      const info = parseDateTime(data.createdAtGte);
      setStartDate(info);
    } else {
      setStartDate({});
    }
    if (data?.createdAtLt) {
      // `createdAtLt` is stored as the start of the day *after* the selected
      // end date, so subtract a day when rehydrating the picker.
      const info = parseDateTime(data.createdAtLt, true);
      setEndDate(info);
    } else {
      setEndDate({});
    }
  }, [data, popoverProps.show]);

  const handleResetClick = useCallback(() => {
    setStartDate({});
    setEndDate({});
    onSubmit?.({ createdAtGte: undefined, createdAtLt: undefined });
  }, [onSubmit]);

  const handleApplyClick = useCallback(() => {
    if (!onSubmit) return;
    const result = convertDateRange({
      startDateString: startDate.date,
      endDateString: endDate.date,
    });
    onSubmit(result);
  }, [onSubmit, convertDateRange, startDate, endDate]);

  const isDisabled = !startDate.date && !endDate.date;

  return (
    <Wrapper {...popoverProps}>
      <Inner pt={12} px={12}>
        <Flex flexDirection="column" gap={16}>
          <FormElement>
            <FormLabel>Start</FormLabel>
            <DateInput
              size="small"
              value={startDate.date}
              onChange={(value) => setStartDate((s) => ({ ...s, date: value }))}
              renderInside
            />
          </FormElement>
          <FormElement>
            <FormLabel>End</FormLabel>
            <DateInput
              size="small"
              value={endDate.date}
              onChange={(value) => setEndDate((s) => ({ ...s, date: value }))}
              renderInside
            />
          </FormElement>
        </Flex>
      </Inner>
      <PopoverAction>
        <Button size="small" variant="secondary" onClick={handleResetClick}>
          Reset
        </Button>
        <Button size="small" onClick={handleApplyClick} isDisabled={!!isDisabled}>
          Apply
        </Button>
      </PopoverAction>
    </Wrapper>
  );
};
